import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useKpiMetrics } from '../dashboard/useKpiMetrics';
import { useExternalNetworkStats } from '../api/useExternalNetworkStats';
import { useInternalNetworkStats } from '../api/useInternalNetworkStats';
import { useAiApplicationStats } from '../api/useAiApplicationStats';
import {
  AGENT_EXECUTIONS_BY_TARGET_KEY,
  fetchAgentExecutionsByTarget,
} from '../api/useAgentExecutionsByTarget';
import { useTargetId, useTargetType } from '../../store/projectStore';
import { TARGET_TYPE } from '../../utils/target-scope.utils';

export type BottomRibbonStats = {
  assets: number;
  signals: number;
  executions: number;
  findings: number;
};

export type BottomRibbonStatsResult = {
  stats: BottomRibbonStats | null;
  isLoading: boolean;
};

const AGENT_EXECUTIONS_STALE_TIME_MS = 30_000;

function sumFindingsBySeverity({
  critical,
  high,
  medium,
  low,
  info = 0,
}: {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info?: number;
}): number {
  return critical + high + medium + low + info;
}

export function useBottomRibbonStats(): BottomRibbonStatsResult {
  const targetId = useTargetId();
  const targetType = useTargetType();

  const isWeb = targetType === TARGET_TYPE.WEB_APPLICATION;
  const isExternal = targetType === TARGET_TYPE.EXTERNAL_NETWORK;
  const isInternal = targetType === TARGET_TYPE.INTERNAL_NETWORK;
  const isAi = targetType === TARGET_TYPE.AI_APPLICATION;
  const isNetwork = isExternal || isInternal;

  const { data: kpiMetrics, isLoading: isKpiLoading } = useKpiMetrics(
    isWeb && targetId ? targetId : '',
  );
  const { stats: externalStats, isLoading: isExternalLoading } =
    useExternalNetworkStats(isExternal ? (targetId ?? null) : null);
  const { stats: internalStats, isLoading: isInternalLoading } =
    useInternalNetworkStats(isInternal ? (targetId ?? null) : null);
  const { stats: aiStats, isLoading: isAiLoading } = useAiApplicationStats({
    aiApplicationId: isAi ? (targetId ?? null) : null,
    enabled: isAi,
  });
  const { data: agentExecutions } = useQuery({
    queryKey: [AGENT_EXECUTIONS_BY_TARGET_KEY, 'ribbon-count', targetId],
    queryFn: () =>
      fetchAgentExecutionsByTarget({
        targetId: targetId ?? '',
        skip: 0,
        limit: 1,
      }),
    enabled: isNetwork && Boolean(targetId),
    staleTime: AGENT_EXECUTIONS_STALE_TIME_MS,
  });

  return useMemo((): BottomRibbonStatsResult => {
    if (!targetId) {
      return { stats: null, isLoading: false };
    }

    if (isWeb) {
      if (!kpiMetrics) {
        return { stats: null, isLoading: isKpiLoading };
      }

      return {
        stats: {
          assets: kpiMetrics.assets.total,
          signals: kpiMetrics.signals.total,
          executions: kpiMetrics.executions.total,
          findings: kpiMetrics.findings.total,
        },
        isLoading: false,
      };
    }

    if (isExternal) {
      if (!externalStats) {
        return { stats: null, isLoading: isExternalLoading };
      }

      return {
        stats: {
          assets:
            externalStats.totalNetblocks +
            externalStats.totalDomains +
            externalStats.totalHosts +
            externalStats.totalGateways +
            externalStats.totalServices +
            externalStats.totalSoftware,
          signals: externalStats.totalSignals,
          executions: agentExecutions?.totalCount ?? 0,
          findings: sumFindingsBySeverity(
            externalStats.findingsCountsBySeverity,
          ),
        },
        isLoading: false,
      };
    }

    if (isInternal) {
      if (!internalStats) {
        return { stats: null, isLoading: isInternalLoading };
      }

      return {
        stats: {
          assets:
            internalStats.totalUsers +
            internalStats.totalGroups +
            internalStats.totalComputers +
            internalStats.totalOus +
            internalStats.totalDomains +
            internalStats.totalGpos +
            internalStats.totalDomainControllers +
            internalStats.totalCertTemplates +
            internalStats.totalContainers,
          signals: internalStats.signalCount,
          executions: agentExecutions?.totalCount ?? 0,
          findings: internalStats.openFindingCount,
        },
        isLoading: false,
      };
    }

    if (isAi) {
      if (!aiStats) {
        return { stats: null, isLoading: isAiLoading };
      }

      return {
        stats: {
          assets: aiStats.agents + aiStats.components,
          signals: aiStats.signals,
          executions: aiStats.executions,
          findings: aiStats.findings,
        },
        isLoading: false,
      };
    }

    return { stats: null, isLoading: false };
  }, [
    targetId,
    isWeb,
    isExternal,
    isInternal,
    isAi,
    kpiMetrics,
    isKpiLoading,
    externalStats,
    isExternalLoading,
    internalStats,
    isInternalLoading,
    aiStats,
    isAiLoading,
    agentExecutions,
  ]);
}
